import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function PendingRegistrationsManager() {
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchPendingRegistrations();
  }, []);

  // Helper function to calculate expiry date and remaining days
  const calculateSubscriptionDetails = (paymentDate, subscriptionDuration) => {
    if (!paymentDate || !subscriptionDuration) {
      return { expiryDate: null, remainingDays: null };
    }

    const payment = new Date(paymentDate);
    const expiry = new Date(payment);
    expiry.setFullYear(expiry.getFullYear() + subscriptionDuration);

    const today = new Date();
    const timeDiff = expiry.getTime() - today.getTime();
    const remainingDays = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return {
      expiryDate: expiry.toLocaleDateString('en-GB'),
      remainingDays: remainingDays
    };
  };

  const fetchPendingRegistrations = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/pending-registrations`, { headers });

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        alert('Session expired. Please login again.');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminAuth');
        setTimeout(() => window.location.reload(), 1000);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setPendingRegistrations(data);
      }
    } catch (error) {
      console.error('Error fetching pending registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (registrationId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/pending-registrations/${registrationId}/approve`, {
        method: 'POST',
        headers
      });

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        alert('Session expired. Please login again.');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminAuth');
        setTimeout(() => window.location.reload(), 1000);
        return;
      }

      const result = await response.json();

      if (result.success) {
        fetchPendingRegistrations(); // Refresh the list
        setSelectedRegistration(null);
      } else {
        console.error('Failed to approve registration:', result.message);
      }
    } catch (error) {
      console.error('Error approving registration:', error);
    }
  };

  const handleDeny = async (registrationId) => {
    const reason = window.prompt('Please enter a reason for denial (optional):');

    if (reason === null) {
      return; // User cancelled
    }

    try {
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/pending-registrations/${registrationId}/deny`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason }),
      });

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        alert('Session expired. Please login again.');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminAuth');
        setTimeout(() => window.location.reload(), 1000);
        return;
      }

      const result = await response.json();

      if (result.success) {
        fetchPendingRegistrations(); // Refresh the list
        setSelectedRegistration(null);
      } else {
        console.error('Failed to deny registration:', result.message);
      }
    } catch (error) {
      console.error('Error denying registration:', error);
    }
  };

  if (loading) {
    return (
      <div className="admin-section">
        <h2>Pending Registration Requests</h2>
        <p>Loading pending registrations...</p>
      </div>
    );
  }

  // Filter pending registrations based on search query
  const filteredPendingRegistrations = pendingRegistrations.filter(registration => {
    if (!searchQuery.trim()) {
      return true;
    }
    const query = searchQuery.toLowerCase();
    return (
      registration.name.toLowerCase().includes(query) ||
      registration.email.toLowerCase().includes(query) ||
      registration.specialization.toLowerCase().includes(query) ||
      registration.location.toLowerCase().includes(query) ||
      registration.status.toLowerCase().includes(query) ||
      (registration.barcode && registration.barcode.toLowerCase().includes(query)) ||
      (registration.providerId && registration.providerId.toLowerCase().includes(query)) ||
      (registration.phone && registration.phone.toLowerCase().includes(query))
    );
  });

  return (
    <div className="admin-section">
      <h2 style={{ color: '#2c3e50', marginBottom: '25px' }}>Pending Registration Requests</h2>

      {/* Search Bar */}
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Search by name, email, specialization, location, status, phone, barcode, or provider ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 15px',
            fontSize: '14px',
            border: '2px solid #ddd',
            borderRadius: '8px',
            outline: 'none',
            transition: 'border-color 0.3s',
          }}
          onFocus={(e) => e.target.style.borderColor = '#f39c12'}
          onBlur={(e) => e.target.style.borderColor = '#ddd'}
        />
        {searchQuery && (
          <p style={{ marginTop: '10px', fontSize: '14px', color: '#555' }}>
            Showing {filteredPendingRegistrations.length} of {pendingRegistrations.length} pending registrations
          </p>
        )}
      </div>

      {pendingRegistrations.length === 0 ? (
        <div style={{
          backgroundColor: '#e8f8f5',
          border: '2px solid #27ae60',
          borderRadius: '8px',
          padding: '20px',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: '16px', color: '#27ae60', margin: 0 }}>
            No pending registration requests at this time.
          </p>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '20px', color: '#555' }}>
            <p>Total pending requests: <strong>{pendingRegistrations.length}</strong></p>
          </div>

          <div style={{ display: 'grid', gap: '20px' }}>
            {filteredPendingRegistrations.map((registration) => {
              // Calculate subscription details if payment is done
              const subscriptionDetails = registration.paymentDate && registration.subscriptionDuration
                ? calculateSubscriptionDetails(registration.paymentDate, registration.subscriptionDuration)
                : { expiryDate: null, remainingDays: null };

              return (
              <div
                key={registration.id}
                style={{
                  border: '2px solid #f39c12',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: '#fffbf0',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                  <div>
                    <h3 style={{ color: '#2c3e50', margin: '0 0 10px 0', fontSize: '20px' }}>
                      {registration.name}
                    </h3>
                    <p style={{ margin: '5px 0', color: '#777', fontSize: '14px' }}>
                      <strong>Submitted:</strong> {registration.submittedDate}
                    </p>
                    <p style={{ margin: '5px 0', color: '#777', fontSize: '14px' }}>
                      <strong>Status:</strong> <span style={{
                        color: registration.status === 'Payment Done-Pending Approval' ? '#e67e22' :
                               registration.status === 'Approved-Pending Payment' ? '#3498db' :
                               registration.status === 'Pending' ? '#f39c12' : '#7f8c8d',
                        fontWeight: 'bold'
                      }}>{registration.status}</span>
                    </p>
                    {registration.approvalStatus && (
                      <p style={{ margin: '5px 0', color: '#777', fontSize: '14px' }}>
                        <strong>Approval:</strong> <span style={{ color: registration.approvalStatus === 'Approved' ? '#27ae60' : '#f39c12', fontWeight: 'bold' }}>{registration.approvalStatus}</span>
                      </p>
                    )}
                    {registration.paymentStatus && (
                      <p style={{ margin: '5px 0', color: '#777', fontSize: '14px' }}>
                        <strong>Payment:</strong> <span style={{ color: registration.paymentStatus === 'Done' ? '#27ae60' : '#e74c3c', fontWeight: 'bold' }}>{registration.paymentStatus}</span>
                      </p>
                    )}
                    {registration.packageName && (
                      <div style={{
                        margin: '10px 0',
                        padding: '10px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '5px',
                        fontSize: '14px'
                      }}>
                        <p style={{ margin: '3px 0', color: '#555' }}>
                          <strong>Selected Package:</strong> {registration.packageName} — <strong style={{ color: '#27ae60' }}>₹{registration.packageAmount?.toLocaleString('en-IN')}</strong>
                        </p>
                        <p style={{ margin: '3px 0', color: '#555' }}>
                          <strong>Amount Claimed Paid:</strong>{' '}
                          <span style={{
                            color: registration.paymentAmountClaimed === registration.packageAmount ? '#27ae60' : '#e74c3c',
                            fontWeight: 'bold'
                          }}>
                            {registration.paymentAmountClaimed != null && registration.paymentAmountClaimed > 0
                              ? `₹${registration.paymentAmountClaimed.toLocaleString('en-IN')}`
                              : 'Not entered'}
                          </span>
                        </p>
                        {registration.paymentStatus === 'Done' &&
                          registration.paymentAmountClaimed !== registration.packageAmount && (
                          <p style={{
                            margin: '5px 0 0 0',
                            padding: '6px 10px',
                            backgroundColor: '#f8d7da',
                            color: '#721c24',
                            border: '1px solid #f5c6cb',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            fontSize: '13px'
                          }}>
                            ⚠️ Amount mismatch — verify UPI receipt before approving
                          </p>
                        )}
                      </div>
                    )}

                    {/* Subscription Details Section */}
                    {registration.status === 'Approved-Payment Done' && registration.paymentDate && (
                      <div style={{
                        marginTop: '15px',
                        padding: '15px',
                        backgroundColor: '#e3f2fd',
                        border: '2px solid #2196f3',
                        borderRadius: '8px'
                      }}>
                        <h4 style={{ margin: '0 0 10px 0', color: '#1976d2', fontSize: '15px' }}>
                          Subscription Details
                        </h4>
                        <div style={{ display: 'grid', gap: '8px' }}>
                          <p style={{ margin: '0', fontSize: '13px', color: '#555' }}>
                            <strong>Active Date (Payment Date):</strong>{' '}
                            <span style={{ color: '#27ae60', fontWeight: 'bold' }}>
                              {new Date(registration.paymentDate).toLocaleDateString('en-GB')}
                            </span>
                          </p>
                          {registration.subscriptionDuration && (
                            <p style={{ margin: '0', fontSize: '13px', color: '#555' }}>
                              <strong>Subscription Plan:</strong>{' '}
                              <span style={{ color: '#2196f3', fontWeight: 'bold' }}>
                                {registration.subscriptionDuration} Year{registration.subscriptionDuration > 1 ? 's' : ''}
                              </span>
                            </p>
                          )}
                          {subscriptionDetails.expiryDate && (
                            <p style={{ margin: '0', fontSize: '13px', color: '#555' }}>
                              <strong>Expiry Date:</strong>{' '}
                              <span style={{ color: '#f39c12', fontWeight: 'bold' }}>
                                {subscriptionDetails.expiryDate}
                              </span>
                            </p>
                          )}
                          {subscriptionDetails.remainingDays !== null && (
                            <p style={{ margin: '0', fontSize: '13px', color: '#555' }}>
                              <strong>Remaining Days:</strong>{' '}
                              <span style={{
                                color: subscriptionDetails.remainingDays < 30 ? '#e74c3c' :
                                       subscriptionDetails.remainingDays < 90 ? '#f39c12' : '#27ae60',
                                fontWeight: 'bold',
                                fontSize: '14px'
                              }}>
                                {subscriptionDetails.remainingDays > 0 ? subscriptionDetails.remainingDays : 0} days
                                {subscriptionDetails.remainingDays <= 0 && ' (EXPIRED)'}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {registration.barcode && registration.providerId && (
                      <div style={{
                        marginTop: '10px',
                        padding: '15px',
                        backgroundColor: '#e8f5e9',
                        border: '2px solid #27ae60',
                        borderRadius: '5px'
                      }}>
                        <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#27ae60', fontWeight: 'bold', textAlign: 'center' }}>
                          SCAN QR CODE TO VIEW DETAILS
                        </p>

                        {/* Scannable QR Code */}
                        <div style={{
                          backgroundColor: 'white',
                          padding: '15px',
                          borderRadius: '5px',
                          textAlign: 'center',
                          marginBottom: '10px'
                        }}>
                          <QRCodeSVG
                            value={`${process.env.REACT_APP_BASE_URL || 'http://localhost:3000'}?provider=${registration.providerId}`}
                            size={150}
                            level="H"
                            includeMargin={true}
                          />
                          <p style={{ margin: '10px 0 0 0', fontSize: '10px', color: '#999' }}>
                            Provider ID: {registration.providerId}
                          </p>
                        </div>
                      </div>
                    )}
                    {registration.barcode && !registration.providerId && (
                      <div style={{
                        marginTop: '10px',
                        padding: '15px',
                        backgroundColor: '#fff3cd',
                        border: '2px solid #f39c12',
                        borderRadius: '5px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#856404' }}>
                          QR Code will be available after payment completion
                        </p>
                        <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#999', fontFamily: 'monospace' }}>
                          Barcode: {registration.barcode}
                        </p>
                      </div>
                    )}
                  </div>
                  {/* Show Approve/Deny buttons only if payment is not completed */}
                  {registration.status !== 'Approved-Payment Done' && (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => handleApprove(registration.id)}
                        style={{
                          backgroundColor: '#27ae60',
                          color: 'white',
                          border: 'none',
                          padding: '10px 20px',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'background-color 0.3s'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#229954'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#27ae60'}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleDeny(registration.id)}
                        style={{
                          backgroundColor: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          padding: '10px 20px',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'background-color 0.3s'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#c0392b'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#e74c3c'}
                      >
                        Deny
                      </button>
                    </div>
                  )}
                  {/* Show completion badge if payment is done */}
                  {registration.status === 'Approved-Payment Done' && (
                    <div style={{
                      backgroundColor: '#e8f5e9',
                      border: '2px solid #27ae60',
                      borderRadius: '8px',
                      padding: '15px 20px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '5px' }}>✓</div>
                      <div style={{ color: '#27ae60', fontWeight: 'bold', fontSize: '14px' }}>
                        Registration Complete
                      </div>
                      <div style={{ color: '#555', fontSize: '12px', marginTop: '5px' }}>
                        Provider Verified & Active
                      </div>
                    </div>
                  )}
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '15px',
                  marginTop: '15px',
                  paddingTop: '15px',
                  borderTop: '1px solid #f39c12'
                }}>
                  <div>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Specialization:</strong><br />
                      {registration.specialization}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Location:</strong><br />
                      {registration.location}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Founded:</strong><br />
                      {registration.founded}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Email:</strong><br />
                      <a href={`mailto:${registration.email}`} style={{ color: '#3498db' }}>
                        {registration.email}
                      </a>
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Phone:</strong><br />
                      {registration.phone}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Website:</strong><br />
                      {registration.website ? (
                        <a href={registration.website} target="_blank" rel="noopener noreferrer" style={{ color: '#3498db' }}>
                          {registration.website}
                        </a>
                      ) : (
                        'N/A'
                      )}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Experience:</strong><br />
                      {registration.completedProjects}
                    </p>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default PendingRegistrationsManager;
